/**
 * Build Map segmentId → riskScore from a risk-map FeatureCollection.
 * @param {object|null|undefined} featureCollection
 * @returns {Map<number, number>}
 */
export function buildSegmentRiskScoreMap(featureCollection) {
  const map = new Map()
  const features = featureCollection?.features
  if (!Array.isArray(features)) return map
  for (const feature of features) {
    const id = Number(feature?.properties?.segmentId)
    if (!Number.isFinite(id)) continue
    map.set(id, Number(feature?.properties?.riskScore ?? 0))
  }
  return map
}

/**
 * Year-over-year risk score change as percent.
 * Returns null when previous-year data is unavailable or % is undefined (prev = 0, curr ≠ 0).
 * @param {number} currentScore
 * @param {number|null|undefined} previousScore
 * @returns {number|null}
 */
export function computeRiskScoreTrendPct(currentScore, previousScore) {
  if (previousScore == null || !Number.isFinite(previousScore)) return null
  if (!Number.isFinite(currentScore)) return null
  if (previousScore === 0) {
    return currentScore === 0 ? 0 : null
  }
  return ((currentScore - previousScore) / Math.abs(previousScore)) * 100
}

/**
 * @param {Map<number, number>|null|undefined} previousScoresBySegmentId
 * @param {number|string|null|undefined} segmentId
 * @param {number} currentScore
 * @returns {number|null}
 */
export function getSegmentRiskTrendPct(previousScoresBySegmentId, segmentId, currentScore) {
  if (!(previousScoresBySegmentId instanceof Map)) return null
  const id = Number(segmentId)
  if (!Number.isFinite(id) || !previousScoresBySegmentId.has(id)) return null
  return computeRiskScoreTrendPct(currentScore, previousScoresBySegmentId.get(id))
}

/**
 * Format trend for display / CSV. Null → "-".
 * @param {number|null|undefined} trendPct
 * @param {{ maximumFractionDigits?: number }} [options]
 * @returns {string}
 */
export function formatRiskTrendPct(trendPct, options = {}) {
  if (trendPct == null || !Number.isFinite(trendPct)) return '-'
  const digits = options.maximumFractionDigits ?? 1
  const abs = Math.abs(trendPct).toLocaleString(undefined, { maximumFractionDigits: digits })
  const sign = trendPct > 0 ? '+' : trendPct < 0 ? '-' : ''
  return `${sign}${abs}%`
}
